import React, { useRef, useState } from "react";
import api, { formatApiError, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { ImagePlus, Trash2 } from "lucide-react";

/**
 * Lets tenant executives (MD/ED) upload a logo that replaces the letter
 * badge in the sidebar. Opened by clicking the badge itself.
 */
export default function TenantLogoDialog({ open, onOpenChange, tenant, onUpdated }) {
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);

  const reset = () => { setFile(null); setPreview(null); };

  const pick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Please choose an image file"); return; }
    if (f.size > 2 * 1024 * 1024) { toast.error("Logo must be under 2 MB"); return; }
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const save = async () => {
    if (!file) return;
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data: up } = await api.post("/files/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const { data: t } = await api.patch("/tenants/me", { logo_file_id: up.id });
      toast.success("Logo updated");
      onUpdated?.(t);
      reset();
      onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const removeLogo = async () => {
    setSaving(true);
    try {
      const { data: t } = await api.patch("/tenants/me", { logo_file_id: null });
      toast.success("Logo removed — letter badge restored");
      onUpdated?.(t);
      reset();
      onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const current = preview || (tenant?.logo_file_id ? fileUrl(tenant.logo_file_id) : null);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="sm:max-w-md" data-testid="tenant-logo-dialog">
        <DialogHeader>
          <DialogTitle>Organisation logo</DialogTitle>
          <DialogDescription>
            Shown in the sidebar for everyone in {tenant?.name || "your organisation"}.
            Square images (PNG/JPG, under 2 MB) look best.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-4">
          <div className="h-20 w-20 rounded-xl border border-gray-200 bg-[#022C22] flex items-center justify-center overflow-hidden shrink-0">
            {current ? (
              <img src={current} alt="Logo preview" className="h-full w-full object-contain"
                data-testid="tenant-logo-preview" />
            ) : (
              <span className="text-2xl font-bold text-[#022C22] bg-amber-500 h-full w-full flex items-center justify-center font-display">
                {(tenant?.name || "A").slice(0, 1).toUpperCase()}
              </span>
            )}
          </div>
          <div className="space-y-2">
            <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml"
              className="hidden" onChange={pick} data-testid="tenant-logo-file-input" />
            <Button variant="outline" size="sm" onClick={() => inputRef.current?.click()}
              data-testid="tenant-logo-choose">
              <ImagePlus size={14} className="mr-1.5" /> Choose image
            </Button>
            {file && <div className="text-xs text-gray-500 truncate max-w-[200px]">{file.name}</div>}
          </div>
        </div>

        <div className="flex justify-between pt-2">
          {tenant?.logo_file_id ? (
            <Button variant="outline" size="sm" onClick={removeLogo} disabled={saving}
              className="text-red-600 border-red-200 hover:bg-red-50" data-testid="tenant-logo-remove">
              <Trash2 size={14} className="mr-1.5" /> Remove logo
            </Button>
          ) : <span />}
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button size="sm" onClick={save} disabled={!file || saving}
              className="bg-emerald-900 hover:bg-emerald-800" data-testid="tenant-logo-save">
              {saving ? "Saving…" : "Save logo"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
